import { useEffect, useState } from 'react';

type Quality = 'online' | 'offline' | 'weak';

interface NetworkStatus {
  online: boolean;
  /** Online but on a slow/saveData connection (2g/slow-2g) */
  weak: boolean;
  quality: Quality;
  /** True for a short window after coming back online */
  justReconnected: boolean;
}

const getConnection = (): any =>
  typeof navigator !== 'undefined'
    ? (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection
    : undefined;

const isWeak = () => {
  const c = getConnection();
  if (!c) return false;
  const type = c.effectiveType as string | undefined;
  return c.saveData === true || type === '2g' || type === 'slow-2g';
};

/**
 * Signal-quality aware network status. Used to replace hard error screens with
 * an in-keeping "signal lost" cue instead of a generic crash message.
 */
export const useNetworkStatus = (): NetworkStatus => {
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [weak, setWeak] = useState(isWeak);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const handleOnline = () => {
      setOnline(true);
      setJustReconnected(true);
      timer = setTimeout(() => setJustReconnected(false), 2600);
    };
    const handleOffline = () => {
      setOnline(false);
      setJustReconnected(false);
    };
    const handleChange = () => setWeak(isWeak());

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const c = getConnection();
    c?.addEventListener?.('change', handleChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      c?.removeEventListener?.('change', handleChange);
    };
  }, []);

  return {
    online,
    weak: online && weak,
    quality: !online ? 'offline' : weak ? 'weak' : 'online',
    justReconnected,
  };
};

export default useNetworkStatus;
